import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { ZoomIn, ZoomOut, Check, X, Loader2 } from 'lucide-react';

/**
 * CircularImageCropModal
 * ---------------------------------------------------------------
 * A dependency-free canvas-based crop dialog. When the user picks
 * an image file the parent opens this modal with the source File.
 * The user can zoom (buttons or wheel) and drag to reposition;
 * the visible circular window is what gets exported as a square
 * PNG Blob via `onConfirm(blob)`.
 *
 *  Props:
 *   - open, onOpenChange:   controlled Dialog state
 *   - file:                 File | null (source image)
 *   - onConfirm(blob):      called with the cropped square Blob
 *   - outputSize:           final Blob dimensions in px (default 512)
 *   - title:                dialog title override
 */
export function CircularImageCropModal({
  open,
  onOpenChange,
  file,
  onConfirm,
  outputSize = 512,
  title = 'Crop your logo',
}) {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [zoom, setZoom] = useState(1);
  // Offset is the top-left of the image in canvas coordinates.
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragRef = useRef({ dragging: false, startX: 0, startY: 0, ox: 0, oy: 0 });
  const [busy, setBusy] = useState(false);
  const CANVAS_SIZE = 320;

  // Load the picked file into an Image element once per file.
  useEffect(() => {
    if (!file || !open) {
      setImgLoaded(false);
      return undefined;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      imgRef.current = img;
      // Fit image so its shortest side matches canvas.
      const scale = CANVAS_SIZE / Math.min(img.width, img.height);
      const w = img.width * scale;
      const h = img.height * scale;
      setZoom(1);
      setOffset({ x: (CANVAS_SIZE - w) / 2, y: (CANVAS_SIZE - h) / 2 });
      setImgLoaded(true);
    };
    img.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file, open]);

  // Redraw whenever zoom/offset change.
  useEffect(() => {
    if (!imgLoaded) return;
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    // Fill dark backdrop so transparent PNGs still show contrast.
    ctx.fillStyle = '#000B18';
    ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    const baseScale = CANVAS_SIZE / Math.min(img.width, img.height);
    const scale = baseScale * zoom;
    const w = img.width * scale;
    const h = img.height * scale;
    ctx.drawImage(img, offset.x, offset.y, w, h);
    // Overlay the circular vignette so users see the exact crop area.
    ctx.save();
    ctx.globalCompositeOperation = 'destination-in';
    ctx.beginPath();
    ctx.arc(CANVAS_SIZE / 2, CANVAS_SIZE / 2, CANVAS_SIZE / 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
    // Ring on top so users see the boundary.
    ctx.strokeStyle = 'rgba(0, 229, 255, 0.85)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(CANVAS_SIZE / 2, CANVAS_SIZE / 2, CANVAS_SIZE / 2 - 1, 0, Math.PI * 2);
    ctx.stroke();
  }, [imgLoaded, zoom, offset]);

  const onPointerDown = (e) => {
    const p = getPoint(e);
    dragRef.current = { dragging: true, startX: p.x, startY: p.y, ox: offset.x, oy: offset.y };
  };
  const onPointerMove = (e) => {
    if (!dragRef.current.dragging) return;
    const p = getPoint(e);
    setOffset({
      x: dragRef.current.ox + (p.x - dragRef.current.startX),
      y: dragRef.current.oy + (p.y - dragRef.current.startY),
    });
  };
  const stopDrag = () => { dragRef.current.dragging = false; };

  const bumpZoom = (delta) => setZoom((z) => Math.min(4, Math.max(0.5, z + delta)));

  const doConfirm = async () => {
    const img = imgRef.current;
    if (!img) return;
    setBusy(true);
    try {
      // Render into a hidden square canvas at `outputSize` and export.
      const out = document.createElement('canvas');
      out.width = outputSize;
      out.height = outputSize;
      const octx = out.getContext('2d');
      const baseScale = CANVAS_SIZE / Math.min(img.width, img.height);
      const scale = baseScale * zoom;
      const ratio = outputSize / CANVAS_SIZE;
      // Draw the same view scaled up.
      octx.fillStyle = '#000B18';
      octx.fillRect(0, 0, outputSize, outputSize);
      octx.drawImage(img, offset.x * ratio, offset.y * ratio, img.width * scale * ratio, img.height * scale * ratio);
      // Clip to circle.
      octx.save();
      octx.globalCompositeOperation = 'destination-in';
      octx.beginPath();
      octx.arc(outputSize / 2, outputSize / 2, outputSize / 2, 0, Math.PI * 2);
      octx.fill();
      octx.restore();
      out.toBlob(async (blob) => {
        if (blob) await onConfirm(blob);
        setBusy(false);
        onOpenChange(false);
      }, 'image/png', 0.92);
    } catch {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#041A3B] border-white/10 max-w-md" data-testid="circular-crop-modal">
        <DialogHeader>
          <DialogTitle className="text-white text-base">{title}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col items-center gap-4">
          <canvas
            ref={canvasRef}
            width={CANVAS_SIZE}
            height={CANVAS_SIZE}
            className="rounded-lg touch-none cursor-move bg-[#000B18]"
            data-testid="crop-canvas"
            onMouseDown={onPointerDown}
            onMouseMove={onPointerMove}
            onMouseUp={stopDrag}
            onMouseLeave={stopDrag}
            onTouchStart={onPointerDown}
            onTouchMove={onPointerMove}
            onTouchEnd={stopDrag}
            onWheel={(e) => { e.preventDefault(); bumpZoom(e.deltaY > 0 ? -0.05 : 0.05); }}
          />
          <div className="flex items-center gap-3">
            <Button type="button" variant="ghost" onClick={() => bumpZoom(-0.15)}
              className="text-[#A0B4CC] hover:text-white" data-testid="crop-zoom-out">
              <ZoomOut className="w-4 h-4" />
            </Button>
            <input
              type="range" min="0.5" max="4" step="0.05"
              value={zoom} onChange={(e) => setZoom(parseFloat(e.target.value))}
              className="w-40 accent-[#00E5FF]"
              data-testid="crop-zoom-range"
            />
            <Button type="button" variant="ghost" onClick={() => bumpZoom(0.15)}
              className="text-[#A0B4CC] hover:text-white" data-testid="crop-zoom-in">
              <ZoomIn className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-[10px] text-[#5C7599] text-center">
            Drag to reposition · pinch or use the slider to zoom · everything inside the cyan ring becomes your logo.
          </p>
          <div className="flex gap-2 w-full">
            <Button type="button" variant="ghost" className="flex-1 text-[#A0B4CC] hover:text-white"
              onClick={() => onOpenChange(false)} data-testid="crop-cancel-btn">
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
            <Button type="button" onClick={doConfirm} disabled={busy || !imgLoaded}
              className="flex-1 bg-[#00E5FF] text-[#001627] hover:bg-[#00B8D4]" data-testid="crop-confirm-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
              Save crop
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function getPoint(e) {
  if (e.touches?.[0]) return { x: e.touches[0].clientX, y: e.touches[0].clientY };
  return { x: e.clientX, y: e.clientY };
}

export default CircularImageCropModal;
